/**
 * ScheduleTab - Weekly workout preferences and availability
 *
 * Contains:
 * - Weekly workout preferences (7 days)
 * - Preferred long day selector
 * - Training mix (run/bike ratio)
 */

import {
  Box,
  MenuItem,
  Slider,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { DayOfWeek, ScheduleEvent, UserProfile } from '@shared/types';
import React from 'react';
import EnvironmentPreferencesMatrix from '../../EnvironmentPreferencesMatrix';
import { ScheduleAgenda } from '../../ScheduleAgenda';
import WorkoutPreferencesMatrix from '../../WorkoutPreferencesMatrix';

interface ScheduleTabProps {
  profile: UserProfile;
  onUpdate: (updates: Partial<UserProfile>, requiresConfirmation?: boolean) => void;
  pendingChanges: Set<string>;
}

const DAYS_OF_WEEK: DayOfWeek[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const ScheduleTab: React.FC<ScheduleTabProps> = ({ profile, onUpdate, pendingChanges }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const runPercent = profile.trainingRatio?.running ?? 100;

  const handleTrainingRatioChange = (value: number) => {
    const bikePercent = 100 - value;
    onUpdate({ trainingRatio: { running: value, cycling: bikePercent } }, true);
  };

  const handlePreferencesChange = (updates: Partial<UserProfile>) => {
    // Weekly preferences require confirmation as they affect the plan
    onUpdate(updates, true);
  };

  const handleLongDayChange = (day: DayOfWeek) => {
    // Preferred long day requires confirmation
    onUpdate({ preferredLongDay: day }, true);
  };

  const handleScheduleEventsChange = (events: ScheduleEvent[]) => {
    // Schedule events require confirmation as they affect the plan
    onUpdate({ scheduleEvents: events }, true);
  };

  const hasPendingScheduleChanges =
    pendingChanges.has('weeklyWorkoutPreferences') || pendingChanges.has('preferredLongDay');

  // Schedule Agenda component - rendered at top on desktop, bottom on mobile
  const scheduleAgendaSection = (
    <Box
      sx={{
        mb: isMobile ? 0 : 3,
        mt: isMobile ? 3 : 0,
        p: 2,
        border: 1,
        borderColor: pendingChanges.has('scheduleEvents') ? 'warning.main' : 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        📆 Schedule Overrides
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Mark specific dates as rest, long, hard, or base training days. These override your weekly
        preferences.
      </Typography>
      <ScheduleAgenda
        scheduleEvents={profile.scheduleEvents || []}
        onEventsChange={handleScheduleEventsChange}
        trainingGoal={profile.trainingGoal}
      />
    </Box>
  );

  return (
    <Box>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          📅 Weekly Schedule
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Set your availability and preferred workout types for each day.
        </Typography>
      </Box>

      {/* Training Mix - At the top */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          border: 1,
          borderColor: pendingChanges.has('trainingRatio') ? 'warning.main' : 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Training Mix
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          How to split your training between running and cycling
        </Typography>

        <Box sx={{ px: 1 }}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" fontWeight={500}>
              🚴 Cycling {100 - runPercent}%
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              🏃 Running {runPercent}%
            </Typography>
          </Box>
          <Slider
            value={runPercent}
            onChange={(_, value) => handleTrainingRatioChange(value as number)}
            min={0}
            max={100}
            step={10}
            marks={[
              { value: 0, label: 'Bike' },
              { value: 50, label: '50/50' },
              { value: 100, label: 'Run' },
            ]}
            aria-label="Training ratio slider"
            sx={{
              '& .MuiSlider-markLabel': {
                fontSize: '0.7rem',
              },
            }}
          />
        </Box>
      </Box>

      {/* Preferred Long Day */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          select
          label="Preferred Long Workout Day"
          value={profile.preferredLongDay ?? 'Saturday'}
          onChange={e => handleLongDayChange(e.target.value as DayOfWeek)}
          helperText="Which day works best for your longest workout?"
          size="small"
          aria-label="Preferred day for long workouts"
          sx={{
            '& .MuiOutlinedInput-root': hasPendingScheduleChanges
              ? {
                  borderColor: 'warning.main',
                  '& fieldset': { borderColor: 'warning.main' },
                }
              : {},
          }}
        >
          {DAYS_OF_WEEK.map(day => (
            <MenuItem key={day} value={day}>
              {day}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Weekly Workout Preferences - Matrix Grid */}
      <Box
        sx={{
          p: 2,
          border: 1,
          borderColor: hasPendingScheduleChanges ? 'warning.main' : 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
          Daily Preferences
        </Typography>
        <WorkoutPreferencesMatrix profile={profile} onChange={handlePreferencesChange} />
      </Box>

      {/* Environment Preferences - Indoor/Outdoor per day */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          border: 1,
          borderColor: pendingChanges.has('weeklyEnvironmentPreferences')
            ? 'warning.main'
            : 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Environment Preferences
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose indoor or outdoor training per day (rest days are skipped)
        </Typography>
        <EnvironmentPreferencesMatrix profile={profile} onChange={handlePreferencesChange} />
      </Box>

      {/* Help text */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          💡 Tip: The plan engine will assign workouts based on your preferences. Hard workouts are
          scheduled on days marked for intense training, with adequate rest between them.
        </Typography>
      </Box>

      {/* Schedule Agenda - Mobile: at bottom */}
      {scheduleAgendaSection}
    </Box>
  );
};

export default ScheduleTab;
